import { useState } from 'react';
import { X, ChevronRight } from 'lucide-react';

const STEPS = [
  {
    num: '1',
    title: '문서 업로드',
    short: '업로드',
    desc: 'PDF 또는 이미지 문서를 업로드합니다.',
    detail: '업로드된 문서는 자동으로 문서 타입이 분류되고, 두 AI 모델에 동시 전송됩니다.',
  },
  {
    num: '2',
    title: '듀얼 추출',
    short: 'Flash+Pro',
    desc: 'Gemini Flash와 Pro가 동시에 데이터를 추출합니다.',
    detail: '두 모델이 독립적으로 같은 문서를 분석하여, 서로 다른 관점에서 필드를 추출합니다. 병렬 실행으로 속도 저하 없이 정확도를 높입니다.',
  },
  {
    num: '3',
    title: '교차검증 + AI Judge',
    short: '검증',
    desc: '두 모델의 결과를 비교하고 불일치를 자동 해결합니다.',
    detail: '규칙 기반 해결 — 포맷·단위·주소 축약·빈값 등 6가지 규칙으로 자동 해결합니다. 규칙으로 안 되면 AI Judge가 원본 이미지를 보고 가중 다수결로 판정합니다.',
  },
  {
    num: '4',
    title: '품질 등급',
    short: '등급',
    desc: '검증 결과에 따라 Gold / Silver / Bronze / 사용자확인 등급을 부여합니다.',
    detail: 'Gold — 100% 일치 또는 사용자 확인. Silver — 전부 자동 해결, 85%+. Bronze — 부분 해결, 70%+. 사용자 확인 — 미해결 필드 존재, 확인 시 Gold로 승격.',
  },
  {
    num: '5',
    title: '증류 데이터 축적',
    short: '저장',
    desc: 'Gold/Silver/Bronze 등급 데이터가 자동으로 DB에 축적됩니다.',
    detail: '문서를 추출할 때마다 검증된 데이터가 자동으로 쌓입니다. 설정 > 증류 파이프라인에서 현황을 확인할 수 있습니다.',
  },
  {
    num: '6',
    title: 'Qwen 모델 학습',
    short: '학습',
    desc: '축적된 데이터로 Qwen3-VL 모델을 파인튜닝합니다.',
    detail: 'Gemini(Teacher)가 추출한 정답을 Qwen(Student)이 학습(SFT). 이후 GRPO 강화학습으로 정확도를 더 높입니다. 학습 완료 후 Gemini 없이 독립 추출이 가능해집니다.',
  },
];

const HarnessWorkflowGuide = ({ className = '' }) => {
  const [open, setOpen] = useState(false);
  const [expandedStep, setExpandedStep] = useState(null);

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-300 hover:border-gray-500 text-gray-400 hover:text-gray-600 transition-colors text-[10px] font-bold ${className}`}
        title="하네스 워크플로우 안내"
      >
        ?
      </button>

      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30" onClick={() => setOpen(false)}>
          <div
            className="bg-white rounded-xl shadow-xl w-[560px] max-h-[80vh] overflow-hidden flex flex-col border border-gray-200"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-gray-800">하네스 엔지니어링 워크플로우</h2>
                <p className="text-[11px] text-gray-400 mt-0.5">문서 업로드 → AI 검증 → 데이터 축적 → 모델 학습</p>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>

            {/* Flow bar */}
            <div className="px-5 py-3 border-b border-gray-50 bg-gray-50/50">
              <div className="flex items-center justify-between">
                {STEPS.map((step, i) => (
                  <div key={i} className="flex items-center">
                    <button
                      onClick={() => setExpandedStep(expandedStep === i ? null : i)}
                      className={`flex items-center gap-1 px-1.5 py-1 rounded text-[10px] transition-colors
                        ${expandedStep === i ? 'bg-gray-200 text-gray-800 font-semibold' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold
                        ${expandedStep === i ? 'bg-gray-700 text-white' : 'bg-gray-300 text-white'}`}>
                        {step.num}
                      </span>
                      <span className="hidden sm:inline">{step.short}</span>
                    </button>
                    {i < STEPS.length - 1 && <ChevronRight size={10} className="text-gray-300 mx-0.5" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Steps */}
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
              {STEPS.map((step, i) => {
                const isExpanded = expandedStep === i;
                return (
                  <div
                    key={i}
                    className={`border rounded-lg transition-all cursor-pointer
                      ${isExpanded ? 'bg-gray-50 border-gray-300' : 'border-gray-100 hover:border-gray-200'}`}
                    onClick={() => setExpandedStep(isExpanded ? null : i)}
                  >
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0
                        ${isExpanded ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {step.num}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-gray-800">{step.title}</div>
                        <div className="text-[11px] text-gray-400">{step.desc}</div>
                      </div>
                      <ChevronRight
                        size={12}
                        className={`text-gray-300 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 ml-8 text-[11px] text-gray-500 leading-relaxed border-t border-gray-100">
                        {step.detail}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Notes */}
              <div className="border border-gray-100 rounded-lg p-3 mt-3 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 text-xs mt-px">*</span>
                  <p className="text-[11px] text-gray-500">문서를 추출할 때마다 자동 실행됩니다. 축적 현황은 <span className="font-medium text-gray-600">설정 &gt; 증류 파이프라인</span>에서 확인하세요.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 text-xs mt-px">*</span>
                  <p className="text-[11px] text-gray-500">"사용자 확인 필요" 결과를 검토하면 <span className="font-medium text-gray-600">Gold 등급</span>으로 저장됩니다. 가장 가치 있는 학습 데이터입니다.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HarnessWorkflowGuide;
